/**
 * Корень админки: сессия и выбор раздела.
 *
 * Токен проверяется походом на `/admin/me` при каждой загрузке страницы,
 * а не тем, что он просто лежит в хранилище. Разница видна в единственном,
 * но важном случае: администратора разжаловали или забанили, пока вкладка
 * была закрыта. Гвард на сервере сверяет роль с базой на каждом запросе,
 * и без стартовой проверки админка показала бы меню, а на первом же
 * действии — ошибку.
 */

import { useCallback, useEffect, useState } from 'react'
import { getToken, onSessionExpired, request, setToken } from './api/client.js'
import type { AdminAccount } from './api/types.js'
import { Layout } from './components/Layout.js'
import { Notice } from './components/ui.js'
import { useRoute } from './router.js'
import { CheatLog } from './sections/CheatLog.js'
import { Economy } from './sections/Economy.js'
import { Generation } from './sections/Generation.js'
import { Login } from './sections/Login.js'
import { PlayerCardView } from './sections/PlayerCard.js'
import { Players } from './sections/Players.js'
import { Scenes } from './sections/Scenes.js'

type State =
  | { status: 'checking' }
  | { status: 'anonymous'; reason: string | null }
  | { status: 'ready'; admin: AdminAccount }

export function App(): JSX.Element {
  const [state, setState] = useState<State>({ status: 'checking' })
  const route = useRoute()

  const check = useCallback(async (): Promise<void> => {
    if (getToken() === null) {
      setState({ status: 'anonymous', reason: null })
      return
    }

    try {
      const admin = await request<AdminAccount>('/admin/me')
      setState({ status: 'ready', admin })
    } catch (err) {
      setToken(null)
      setState({ status: 'anonymous', reason: err instanceof Error ? err.message : String(err) })
    }
  }, [])

  useEffect(() => {
    void check()
  }, [check])

  // Любой 401 в глубине разделов возвращает на экран входа: сессия админки
  // живёт 8 часов, и истечь она может посреди работы.
  useEffect(
    () =>
      onSessionExpired(() => {
        setState({ status: 'anonymous', reason: 'Session expired — sign in again.' })
      }),
    [],
  )

  if (state.status === 'checking') {
    return (
      <div className="shell">
        <div className="muted">Checking your session…</div>
      </div>
    )
  }

  if (state.status === 'anonymous') {
    return (
      <>
        {state.reason !== null && (
          <div className="shell" style={{ minHeight: 0, paddingBottom: 0 }}>
            <div style={{ width: '100%', maxWidth: 380 }}>
              <Notice tone="warn">{state.reason}</Notice>
            </div>
          </div>
        )}
        <Login onSuccess={() => void check()} />
      </>
    )
  }

  return (
    <Layout
      route={route}
      admin={state.admin}
      onLogout={() => {
        setToken(null)
        setState({ status: 'anonymous', reason: null })
      }}
    >
      {route.name === 'economy' && <Economy />}
      {route.name === 'scenes' && <Scenes />}
      {route.name === 'players' && <Players />}
      {/* key — чтобы переход между карточками сбрасывал состояние форм:
          иначе сумма, набранная для одного игрока, осталась бы в поле
          у другого. */}
      {route.name === 'player' && <PlayerCardView id={route.id} key={route.id} />}
      {route.name === 'cheats' && <CheatLog />}
      {route.name === 'generation' && <Generation />}
    </Layout>
  )
}
